import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate, useLocation, Outlet } from 'react-router';
import {
  ChevronDown, Settings, HelpCircle, LogOut, User, Menu, X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase, Profile } from '../../lib/supabase';
import { useTheme } from '../../contexts/ThemeContext';
import { Sentry } from '@/lib/analytics';
import { useRolePermissions, getProfileDisplayName } from '../../hooks/useRolePermissions';

// DotGrid background component
const DotGrid = () => (
  <div
    className="fixed inset-0 pointer-events-none"
    style={{
      backgroundImage: 'radial-gradient(circle, rgba(var(--theme-primary-rgb),0.04) 1px, transparent 1px)',
      backgroundSize: '24px 24px',
      zIndex: 0,
    }}
  />
);

export interface DashboardLayoutContext {
  profile: Profile & { agency_id?: string | null };
}

interface Reminder {
  id: string;
  text: string;
  done: boolean;
}

export function DashboardLayout() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const profileRef = useRef<HTMLDivElement>(null);
  const permissions = useRolePermissions(profile);

  // Filter nav items based on permissions
  const navItems = [
    { labelKey: 'nav.home', path: '/kovadash', show: true },
    { labelKey: 'nav.clients', path: '/clients', show: permissions.canViewClients },
    { labelKey: 'nav.suppliers', path: '/suppliers', show: permissions.canViewSuppliers },
    { labelKey: 'nav.guides', path: '/guides', show: permissions.canViewGuides },
    { labelKey: 'nav.travel', path: '/travel', show: permissions.canViewTravel },
  ].filter(item => item.show);

  // Fetch profile function
  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/');
        return;
      }

      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setProfile(profileData);
    } catch (error) {
      console.error('Error fetching profile:', error);
      navigate('/');
    }
  };

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  // Refetch profile when location changes (to pick up profile updates from Settings)
  useEffect(() => {
    if (profile) {
      // Only refetch if profile already loaded (not on initial mount)
      fetchProfile();
    }
  }, [location.pathname]);

  // Listen for profile header updates from Settings
  useEffect(() => {
    const handleProfileHeaderUpdate = () => {
      fetchProfile();
    };

    window.addEventListener('profileHeaderUpdate', handleProfileHeaderUpdate);
    return () => window.removeEventListener('profileHeaderUpdate', handleProfileHeaderUpdate);
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = () => {
    // Navigate first to avoid showing default branding while signing out
    navigate('/');
    supabase.auth.signOut().catch(error => {
      Sentry.captureException(error, { extra: { context: 'Sign out' } });
    });
  };

  const getInitials = () => {
    if (!profile) return 'KV';
    return `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase();
  };

  const pending = reminders.filter(r => !r.done).length;

  if (!profile) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: 'var(--theme-background)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ width: '240px', textAlign: 'center' }}>
          <p style={{
            fontSize: '10px',
            color: 'rgba(var(--theme-text-rgb),0.6)',
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            marginBottom: '12px'
          }}>
            {t('nav.loading')}
          </p>
          <div style={{
            height: '2px',
            backgroundColor: 'rgba(var(--theme-primary-rgb),0.1)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: '40%',
              backgroundColor: 'rgba(var(--theme-primary-rgb),0.4)',
              animation: 'loading 1.5s ease-in-out infinite',
            }} />
          </div>
        </div>
        <style>{`
          @keyframes loading {
            0%, 100% { transform: translateX(-100%); }
            50% { transform: translateX(250%); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--theme-background)] relative">
      <DotGrid />

      {/* ── Top Navigation ──────────────────────────────────────── */}
      <nav
        className="sticky top-0 z-50 bg-[var(--theme-background)]/96 backdrop-blur-md"
        style={{ borderBottom: '1px solid rgba(var(--theme-primary-rgb),0.08)' }}
      >
        <div className="max-w-[1380px] mx-auto px-4 md:px-8 h-14 md:h-16 flex items-center justify-between">

          {/* Left: Hamburger (mobile) + Logo/Wordmark + nav links */}
          <div className="flex items-center gap-4 md:gap-10">
            {/* Mobile hamburger menu */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-8 h-8 rounded-sm transition-colors"
              style={{
                background: mobileMenuOpen ? 'rgba(var(--theme-primary-rgb),0.08)' : 'transparent',
              }}
            >
              {mobileMenuOpen ? (
                <X size={18} strokeWidth={1.5} className="text-[var(--theme-primary)]/70" />
              ) : (
                <Menu size={18} strokeWidth={1.5} className="text-[var(--theme-primary)]/70" />
              )}
            </button>
            <div className="flex items-center">
              {theme.branding.logoUrl ? (
                <img
                  src={theme.branding.logoUrl}
                  alt={theme.branding.brandName}
                  className="h-12 object-contain"
                  style={{ maxWidth: '200px' }}
                />
              ) : (
                <h1
                  className="text-[var(--theme-primary)] uppercase font-medium"
                  style={{ fontSize: '16px', letterSpacing: '0.55em' }}
                >
                  {theme.branding.brandName}
                </h1>
              )}
            </div>
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.labelKey}
                    onClick={() => {
                      navigate(item.path);
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.color = 'rgba(var(--theme-text-rgb),0.8)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.color = 'rgba(var(--theme-text-rgb),0.55)';
                      }
                    }}
                    className="px-3 py-1.5 transition-colors uppercase tracking-[0.18em] rounded-sm font-medium"
                    style={{
                      fontSize: '10px',
                      color: isActive ? 'rgba(var(--theme-text-rgb),0.9)' : 'rgba(var(--theme-text-rgb),0.55)',
                      background: isActive ? 'rgba(var(--theme-primary-rgb),0.05)' : 'transparent',
                    }}
                  >
                    {t(item.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Profile dropdown */}
          <div className="flex items-center gap-5">
            {/* Reminders quick count */}
            {pending > 0 && (
              <div className="hidden sm:flex items-center gap-2">
                <div
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: 'rgba(var(--theme-primary-rgb),0.4)' }}
                />
                <span className="text-[var(--theme-text)]/80 uppercase tracking-[0.18em]" style={{ fontSize: '9px' }}>
                  {pending} {t('nav.pending')}
                </span>
              </div>
            )}

            {/* Vertical separator */}
            <div className="hidden sm:block w-[1px] h-5" style={{ background: 'rgba(var(--theme-primary-rgb),0.1)' }} />

            {/* Profile */}
            <div ref={profileRef} className="relative">
              <button
                onClick={() => setProfileOpen(v => !v)}
                className="flex items-center gap-2.5 group"
              >
                {/* Avatar circle */}
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden"
                  style={{
                    background: profile.profile_picture_url ? 'transparent' : 'rgba(var(--theme-primary-rgb),0.08)',
                    border: '1px solid rgba(var(--theme-primary-rgb),0.14)',
                  }}
                >
                  {profile.profile_picture_url ? (
                    <img
                      src={profile.profile_picture_url}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-[var(--theme-primary)]/65 font-medium" style={{ fontSize: '11px' }}>
                      {getInitials()}
                    </span>
                  )}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-[var(--theme-text)]/75" style={{ fontSize: '12px' }}>
                    {profile.first_name} {profile.last_name?.[0]}.
                  </p>
                </div>
                <ChevronDown
                  size={12}
                  strokeWidth={1.5}
                  className="text-[var(--theme-primary)]/50 transition-transform duration-300"
                  style={{ transform: profileOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </button>

              {/* Dropdown panel */}
              <AnimatePresence>
                {profileOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.97 }}
                    transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute right-0 top-full mt-2.5 w-56 overflow-hidden"
                    style={{
                      borderRadius: '4px',
                      background: 'var(--theme-background)',
                      border: '1px solid rgba(var(--theme-primary-rgb),0.1)',
                      boxShadow: '0 12px 40px rgba(var(--theme-primary-rgb),0.1), 0 2px 8px rgba(var(--theme-primary-rgb),0.06)',
                    }}
                  >
                    {/* User header */}
                    <div className="px-4 py-4" style={{ borderBottom: '1px solid rgba(var(--theme-primary-rgb),0.07)' }}>
                      <div className="flex items-center gap-3 mb-1">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
                          style={{
                            background: profile.profile_picture_url ? 'transparent' : 'rgba(var(--theme-primary-rgb),0.08)',
                            border: '1px solid rgba(var(--theme-primary-rgb),0.12)'
                          }}
                        >
                          {profile.profile_picture_url ? (
                            <img
                              src={profile.profile_picture_url}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-[var(--theme-primary)]/55" style={{ fontSize: '10px' }}>{getInitials()}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-[var(--theme-text)]" style={{ fontSize: '13px' }}>
                            {profile.first_name} {profile.last_name}
                          </p>
                          <p className="text-[var(--theme-text)]/70 font-light" style={{ fontSize: '10px' }}>
                            {permissions.isCollaborator ? t('nav.collaborator') : profile.agency_name}
                          </p>
                        </div>
                      </div>
                      {!permissions.isCollaborator && profile.iata_number && (
                      <div
                        className="mt-2 px-2 py-1 rounded-sm text-center"
                        style={{ background: 'rgba(var(--theme-primary-rgb),0.04)', border: '1px solid rgba(var(--theme-primary-rgb),0.07)' }}
                      >
                        <span className="text-[var(--theme-text)]/70 uppercase tracking-[0.15em]" style={{ fontSize: '10px' }}>
                          IATA · {profile.iata_number}
                        </span>
                      </div>
                      )}
                    </div>

                    {/* Menu items */}
                    <div className="py-2">
                      <button
                        onClick={() => {
                          navigate('/settings');
                          setProfileOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
                        style={{ background: 'transparent' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(var(--theme-primary-rgb),0.04)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <Settings size={13} strokeWidth={1.4} className="text-[var(--theme-primary)]/75" />
                        <span className="text-[var(--theme-text)]/65" style={{ fontSize: '12px' }}>
                          {t('nav.settings')}
                        </span>
                      </button>
                    </div>

                    <div style={{ borderTop: '1px solid rgba(var(--theme-primary-rgb),0.07)' }} className="py-2">
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
                        style={{ background: 'transparent' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(var(--theme-primary-rgb),0.04)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <LogOut size={13} strokeWidth={1.4} className="text-[var(--theme-primary)]/70" />
                        <span className="text-[var(--theme-text)]/70" style={{ fontSize: '12px' }}>
                          {t('nav.signOut')}
                        </span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Mobile Navigation Menu ─────────────────────────────── */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden sticky top-14 z-40 overflow-hidden"
            style={{
              background: 'var(--theme-background)',
              borderBottom: '1px solid rgba(var(--theme-primary-rgb),0.08)',
            }}
          >
            <div className="px-4 py-3 flex flex-col gap-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.labelKey}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className="px-3 py-2.5 text-left transition-colors uppercase tracking-[0.18em] rounded-sm font-medium"
                    style={{
                      fontSize: '11px',
                      color: isActive ? 'rgba(var(--theme-text-rgb),0.9)' : 'rgba(var(--theme-text-rgb),0.55)',
                      background: isActive ? 'rgba(var(--theme-primary-rgb),0.05)' : 'transparent',
                    }}
                  >
                    {t(item.labelKey)}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Main Content ────────────────────────────────────────── */}
      <div className="relative z-10">
        <Outlet context={{ profile } satisfies DashboardLayoutContext} />
      </div>
    </div>
  );
}
